import React, { useEffect, useState } from "react";
import { Home, Flame, Droplets, ChevronDown, ChevronUp, Sparkles, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import ShareMenu from "./ShareMenu";

const ICONS = { Home, Flame, Droplets };
const CLINIC_WA_E164 = "+447445372190";

/** Renders the 3 additional Ruqyah programmes (House Cleaning, Womb Steam,
 *  Vomiting) provided by the clinic owner. Expandable cards. Every programme
 *  has a direct "Ask about this on WhatsApp" CTA so the user funnels to chat. */
const RuqyahProgrammesSection = () => {
  const [progs, setProgs] = useState([]);
  const [open, setOpen] = useState(null);
  const { t, lang } = useLanguage();

  useEffect(() => {
    api
      .get("/ruqyah/programmes", { params: { lang } })
      .then((r) => setProgs(r.data.programmes || []))
      .catch(() => {});
  }, [lang]);

  if (!progs.length) return null;

  const buildWaUrl = (p) => {
    const msg = encodeURIComponent(
      `Assalāmu ʿalaykum, I would like to ask about the "${p.title}" programme at Islamic Cure & Care.`
    );
    return `https://wa.me/${CLINIC_WA_E164.replace("+", "")}?text=${msg}`;
  };

  return (
    <section data-testid="ruqyah-programmes-section" className="mb-16">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">{t("ruqyah_programmes_pill")}</p>
        <h2 className="font-heading text-3xl sm:text-4xl text-forest font-semibold">
          {t("ruqyah_programmes_title")}
        </h2>
        <p className="text-sm text-mute mt-3 max-w-2xl mx-auto">{t("ruqyah_programmes_subtitle")}</p>
      </div>

      <div className="space-y-4">
        {progs.map((p) => {
          const Icon = ICONS[p.icon] || Sparkles;
          const isOpen = open === p.id;
          return (
            <article
              key={p.id}
              data-testid={`programme-card-${p.id}`}
              className="bg-white rounded-2xl border border-gold/25 overflow-hidden shadow-sm"
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : p.id)}
                data-testid={`programme-toggle-${p.id}`}
                className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left hover:bg-cream/30 transition"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: `${p.color}15`, color: p.color }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg sm:text-xl text-forest font-semibold leading-snug">
                      {p.title}
                    </h3>
                    <p className="text-xs text-mute italic mt-0.5">{p.subtitle}</p>
                  </div>
                </div>
                {isOpen ? (
                  <ChevronUp className="w-5 h-5 text-forest flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-forest flex-shrink-0" />
                )}
              </button>

              {isOpen && (
                <div className="px-5 sm:px-6 pb-6 pt-2 space-y-4 text-sm">
                  {p.intro && <p className="text-mute italic leading-relaxed">{p.intro}</p>}

                  {p.ingredients?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-2">
                        {t("section_ingredients")}
                      </p>
                      <ul className="space-y-1">
                        {p.ingredients.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-ink">
                            <span className="text-gold mt-1.5">•</span>
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {p.recitation?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-2">
                        {t("section_recite_each")}
                      </p>
                      <ul className="space-y-1">
                        {p.recitation.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-ink">
                            <span className="text-forest mt-1.5">•</span>
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {p.method?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-2">
                        {t("section_method")}
                      </p>
                      <ol className="space-y-1 list-decimal list-inside text-ink">
                        {p.method.map((it, i) => (
                          <li key={i}>{it}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {p.bonus && (
                    <div className="bg-cream rounded-xl p-4 border border-gold/30">
                      <p className="text-xs uppercase tracking-wider text-gold font-semibold mb-1">
                        {t("section_additional_step")}
                      </p>
                      <p className="text-ink leading-relaxed">{p.bonus}</p>
                    </div>
                  )}

                  {p.caution && (
                    <div className="bg-clay/10 rounded-xl p-4 border border-clay/30">
                      <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-1">
                        {t("section_caution")}
                      </p>
                      <p className="text-ink leading-relaxed">{p.caution}</p>
                    </div>
                  )}

                  {p.audience_warning && <p className="text-xs text-clay italic">{p.audience_warning}</p>}

                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    {p.duration && (
                      <span className="text-xs bg-forest/10 text-forest px-3 py-1 rounded-full">
                        ⏱ {p.duration}
                      </span>
                    )}
                    <a
                      data-testid={`programme-whatsapp-${p.id}`}
                      href={buildWaUrl(p)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-medium px-4 py-2 rounded-full transition"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> {t("ask_on_whatsapp")}
                    </a>
                    <ShareMenu
                      title={p.title}
                      url={typeof window !== "undefined" ? `${window.location.origin}/ruqyah#${p.id}` : ""}
                      compact
                    />
                  </div>

                  {p.closing && (
                    <p className="text-center text-mute italic mt-3 pt-3 border-t border-gold/15">{p.closing}</p>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default RuqyahProgrammesSection;
